import React from 'react';
import { orderItemProps } from './props';
import { TmntText } from '../Common/Text';
import ItemActions from './ItemActions';

/**
 * OrderItem Component
 *
 * Displays a single order item in OrderDetails page OR Return/Exchange view
 */
export default function OrderItem({ orderProduct, text: orderText }) {
  const {
    product, status, quantity,
    isReturnExchangeView,
    returnReason,
    isExchange,
    returnExchangeText,
    shouldDisplayPriceInCard,
  } = orderProduct;

  // Use the passed text prop or fall back to the one from orderProduct
  const displayText = orderText || orderProduct?.text;
  const {
    name, sku, storeItem, imageUrl, productUrl, color, size,
  } = product;

  // Format size and color display
  const sizeColorText = `${size || ''}${size && color ? ', ' : ''}${color || ''}${quantity ? `, Qty:${quantity}` : ''}`;

  // Determine which price to display (original order or return/exchange item price)
  const displayFormattedPrice = isReturnExchangeView
    ? orderProduct.product.formattedPrice
    : product.formattedPrice;

  // Determine container class based on view type and status presence
  const productContentClass = `product-content ${!isReturnExchangeView && status ? 'product-content--with-status' : ''}`;

  // Get Return/Exchange Text
  const exchangeStatusText = isExchange ? returnExchangeText?.exchange : returnExchangeText?.return;

  return (
    <div className="order-details__product-card-container">
      <div className="product-template" data-alignment="horizontal">
        {/* Image Section */}
        <div className="product-image-section order-details__product-card-image">
          {/* Status Badge positioned absolutely relative to this section */}
          {status && !isReturnExchangeView && (
            <div className="badge status" data-attached="true" data-full-width="true" data-text-align="center">
              <span>{status}</span>
            </div>
          )}
          {/* Image */}
          <div className="product-image">
            <a href={productUrl} className="product-link">
              <img src={imageUrl} alt={name} />
            </a>
          </div>
        </div>

        <div className={productContentClass}>
          <div className="product-header-row">
            <div className="product-info-column">
              {!isReturnExchangeView && orderProduct.departmentName && typeof orderProduct.departmentName === 'string' && (
                <div className="product-category">
                  {`${orderProduct.departmentName.charAt(0).toUpperCase()}${orderProduct.departmentName.slice(1)}`}
                </div>
              )}
              <div className="product-name">{name}</div>
              <p className="product-detail">
                <span className="" data-text-key="BAG_SKU">
                  {displayText?.sku?.value}
                </span>
                &nbsp;
                {sku}
              </p>
              {!isReturnExchangeView && storeItem && displayText?.storeItemNumber?.value && (
                <p className="product-detail">
                  <span className="" data-text-key="CAT_STOREITEMNUMBER">
                    {displayText?.storeItemNumber?.value}
                  </span>
                  &nbsp;
                  {storeItem}
                </p>
              )}
              {(size || color || quantity) && (
                <p className="product-detail">
                  {sizeColorText}
                </p>
              )}
              {displayFormattedPrice && (isReturnExchangeView || shouldDisplayPriceInCard) && (
                <p className="product-price">{displayFormattedPrice}</p>
              )}
              {isReturnExchangeView && exchangeStatusText && (
                <p className="order_type_text">
                  {typeof exchangeStatusText === 'object' ? (
                    <TmntText tmnt={exchangeStatusText} />
                  ) : (
                    exchangeStatusText
                  )}
                </p>
              )}
            </div>
            {isReturnExchangeView ? (
              <div className="product-secondary-content">
                <section className="return_item-reason_status">
                  <div className="data-card" data-variant="boxed">
                    <div className="data-card-content">
                      <div className="returns-flow__reason__name_wrapper">
                        <span className="return-reasoncode-label">
                          Return Reason
                          <span>: </span>
                        </span>
                        {returnReason}
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            ) : (
              <div className="item-actions">
                <ItemActions
                  orderProduct={orderProduct}
                  text={displayText}
                />
              </div>
            )}
          </div>
        </div>

        {/* Final Sale Message */}
        {product?.isFinalSaleItem && displayText?.finalSaleMessage && (
          <div
            data-property="GLB_FINAL_SALE_MESSAGING"
            className="final-sale-description-wrapper order-item__final-sale-message"
            data-testid="final-sale-message"
          >
            <TmntText tmnt={displayText?.finalSaleMessage} />
          </div>
        )}
      </div>
    </div>
  );
}

OrderItem.propTypes = orderItemProps;
